import { RootState, useAppDispatch } from '_store';
import apiSagas from '_store/api/sagas';
import { Box, CircularProgress } from '@material-ui/core';
import { RouteComponentProps, Router } from '@reach/router';
import React, { ReactElement, useCallback,useEffect, useState } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';

import CustomToast from './components/items/CustomToast';
import DenmCreatedNotification from './components/items/DenmCreatedNotification';
import PmvStatusNotification from './components/items/PmvStatusNotification';
import SideMenu from './components/routes/SideMenu';
import TopMenu from './components/routes/TopMenu';

const Home: React.FC<RouteComponentProps> = (): ReactElement => {
  const toasts = useSelector((state: RootState) => state.application.toasts);

  return (
    <main className="Main">
      <TopMenu />
      <SideMenu />
      <Box
        style={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          zIndex: 1301,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <DenmCreatedNotification />
        <PmvStatusNotification />
      </Box>
      {toasts.map((t) => (
        <CustomToast key={t.id} id={t.id} title={t.title} />
      ))}
    </main>
  );
};

export default function App(): ReactElement {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [lastTokenCheck, setLastTokenCheck] = useState<Date>(new Date());
  const [lastLoginTime, setLastLoginTime] = useState<Date | null>(null);

  const dispatch = useAppDispatch();

  const { ready, isUserLoggedIn } = useSelector((state: RootState) => ({
    ready: state.application.ready,
    isUserLoggedIn: state.application.userLoggedIn,
  }));

  // Funzione per verificare se è tempo di fare un refresh proattivo
  const shouldForceRefresh = useCallback(() => {
    if (!lastLoginTime) return false;

    const now = new Date();
    const timeSinceLogin = now.getTime() - lastLoginTime.getTime();

    // Forza refresh dopo 25 minuti (5 minuti prima della scadenza dei 30 minuti)
    const twentyFiveMinutes = 25 * 60 * 1000;

    return timeSinceLogin > twentyFiveMinutes;
  }, [lastLoginTime]);

  // Funzione per verificare se il refresh token è scaduto
  const checkRefreshTokenExpiry = useCallback(() => {
    if (!lastLoginTime) return false;

    const now = new Date();
    const timeSinceLogin = now.getTime() - lastLoginTime.getTime();

    // Considera il refresh token scaduto dopo 30 minuti
    const thirtyMinutes = 30 * 60 * 1000;

    return timeSinceLogin > thirtyMinutes;
  }, [lastLoginTime]);

  // Funzione per gestire il refresh del token
  const handleTokenRefresh = useCallback(() => {
    if (!auth.isAuthenticated || !auth.user) return;

    // Se il refresh token è scaduto, forza il re-login
    if (checkRefreshTokenExpiry()) {
      console.log('Refresh token scaduto (30+ minuti), forzo il re-login');
      auth.removeUser();
      auth.signinRedirect();
      return;
    }

    // Aggiorna il timestamp dell'ultimo login se non è già impostato
    if (!lastLoginTime) {
      setLastLoginTime(new Date());
    }

    // Se l'utente è autenticato, procedi con il normale flow
    const action = isUserLoggedIn
      ? apiSagas.actions.renewToken({ token: auth.user?.access_token })
      : apiSagas.actions.createClientFromCredentials({ token: auth.user?.access_token });

    dispatch(action);
  }, [auth, isUserLoggedIn, dispatch, checkRefreshTokenExpiry, lastLoginTime]);

  // Funzione per forzare un refresh proattivo
  const forceProactiveRefresh = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user) return;

    console.log('Forzando refresh proattivo del token...');

    try {
      // Prova a fare il silent refresh
      await auth.signinSilent();
      console.log('Refresh proattivo completato con successo');

      // Aggiorna il timestamp dell'ultimo refresh
      setLastLoginTime(new Date());

      // Aggiorna il token nella sessione
      if (auth.user?.access_token) {
        window.sessionStorage.setItem('token', auth.user.access_token);
      }
    } catch (error) {
      console.error('Errore durante il refresh proattivo:', error);

      // Se il refresh proattivo fallisce, probabilmente il refresh token è scaduto
      if (
        error.message?.includes('invalid_grant') ||
        error.message?.includes('Token is not active')
      ) {
        console.log('Refresh token scaduto durante refresh proattivo, forzo re-login');
        auth.removeUser();
        auth.signinRedirect();
      }
    }
  }, [auth]);

  // Effetto principale per gestire l'autenticazione
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin
    ) {
      auth.signinRedirect();
      setHasTriedSignin(true);
      return;
    }

    if (auth.isAuthenticated) {
      // Se è un nuovo login, aggiorna il timestamp
      if (!lastLoginTime) {
        setLastLoginTime(new Date());
      }

      handleTokenRefresh();
    }
  }, [auth, hasTriedSignin, handleTokenRefresh, lastLoginTime]);

  // Effetto per monitorare periodicamente lo stato dei token e forzare refresh proattivo
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const interval = setInterval(() => {
      const now = new Date();
      const timeSinceLastCheck = now.getTime() - lastTokenCheck.getTime();

      // Controlla ogni 2 minuti
      if (timeSinceLastCheck > 2 * 60 * 1000) {
        setLastTokenCheck(now);

        // Se è tempo di fare un refresh proattivo, fallo
        if (shouldForceRefresh()) {
          forceProactiveRefresh();
        }
        // Se il refresh token è scaduto, forza un nuovo login
        else if (checkRefreshTokenExpiry()) {
          console.log('Refresh token scaduto durante il controllo periodico');
          auth.removeUser();
          auth.signinRedirect();
        }
      }
    }, 60000); // Controlla ogni minuto

    return () => clearInterval(interval);
  }, [auth, lastTokenCheck, shouldForceRefresh, checkRefreshTokenExpiry, forceProactiveRefresh]);

  // Gestisce errori di autenticazione globali
  useEffect(() => {
    const handleAuthError = (event: any) => {
      if (event.detail && event.detail.error) {
        console.error('Errore di autenticazione:', event.detail.error);

        // Se è un errore di token scaduto, forza il re-login
        if (
          event.detail.error.includes('refresh_token') ||
          event.detail.error.includes('invalid_grant') ||
          event.detail.error.includes('Token is not active')
        ) {
          console.log('Errore refresh token, forzo il re-login');
          auth.removeUser();
          auth.signinRedirect();
        }
      }
    };

    // Gestisce eventi di token scaduto dalle chiamate API
    const handleTokenExpired = () => {
      console.log('Token scaduto rilevato dalle API, forzo il re-login');
      auth.removeUser();
      auth.signinRedirect();
    };

    // Ascolta eventi di errore di autenticazione
    window.addEventListener('oidc:error', handleAuthError);
    window.addEventListener('token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('oidc:error', handleAuthError);
      window.removeEventListener('token-expired', handleTokenExpired);
    };
  }, [auth]);

  if (auth.isLoading && !auth.activeNavigator) {
    return <div>Loading...</div>;
  }

  if (!auth.isAuthenticated) {
    return <div>Not authenticated, redirecting to log in</div>;
  }

  if (!ready) {
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <CircularProgress />
        <div style={{ marginTop: '1rem' }}>Loading required data</div>
      </div>
    );
  }

  return (
    <Router className="container">
      <Home path="/" />
    </Router>
  );
}
